/**
 * Tiny pub/sub helper for "migration data was just refreshed" events.
 *
 * A successful Excel upload on the Migration Dashboard should invalidate the derived
 * data shown across every migration tab (progress cards, weekly trend, per-type stats,
 * the Azure performance report's auto-resolved datacenters, the "Last synced" badge in
 * the MigrationsView header, etc.).
 *
 * We keep this intentionally minimal — a single custom-event name plus helpers — so that
 * any existing or future migration tab can opt-in without adding a context / store.
 */

export const MIGRATION_DATA_UPDATED_EVENT = 'migration-data-updated';

/**
 * Dispatch the migration-data-updated event. Safe on SSR (no-op when `window` is missing).
 *
 * @param {object} [detail] - optional payload passed through `event.detail`
 *   (e.g. `{ rowCount, fileName, uploadedAt }` from the upload response).
 */
export function dispatchMigrationDataUpdated(detail = {}) {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(MIGRATION_DATA_UPDATED_EVENT, { detail }));
  } catch {
    // Old browsers may not support CustomEvent constructor; fall back silently.
  }
}

/**
 * Subscribe to the migration-data-updated event for the lifetime of the caller.
 * Returns an unsubscribe function suitable for a useEffect cleanup.
 *
 * @param {(detail:object) => void} handler
 * @returns {() => void}
 */
export function onMigrationDataUpdated(handler) {
  if (typeof window === 'undefined' || typeof handler !== 'function') {
    return () => {};
  }
  const listener = (e) => handler(e?.detail || {});
  window.addEventListener(MIGRATION_DATA_UPDATED_EVENT, listener);
  return () => window.removeEventListener(MIGRATION_DATA_UPDATED_EVENT, listener);
}
